import { useCallback, useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { SOCIAL_ENABLED } from '../lib/featureFlags';
import { supabase } from '../lib/supabaseClient';
import type { SocialPlatform } from '../components/SocialConnectButton';
import { useBusinessTableChanges } from './useBusinessTableChanges';
import { requestVideoFinish } from './useFinishVideoCaption';

const SOCIAL_LIVE_TABLES = ['social_post_drafts', 'social_post_history'] as const;

export interface UpcomingSocialPostSnippet {
  id: string;
  platform: SocialPlatform;
  content: string;
  scheduled_for: string | null;
  source: 'ai' | 'manual' | string | null;
  has_image: boolean;
  media_id: string | null;
  media_ids: string[] | null;
  image_count: number;
  image_url: string | null;
  created_at: string;
  connection_id: string | null;
  /** pending = staged draft; published = live post from history. */
  status: 'pending' | 'published';
  /** The media is a video (image_url points at the file). */
  is_video?: boolean;
  post_format?: 'feed' | 'reel' | 'story';
  /** A bot asked for text on the video; the app still has to draw it in. */
  video_caption_pending?: boolean;
  /** The cover still has to be chosen from the video, or from a named photo. */
  video_thumbnail_pending?: boolean;
  /** Ayrshare id for a published post, used to delete it from the network. */
  ayrshare_post_id?: string | null;
}

type DraftRow = {
  id: string;
  platform: string;
  content: string;
  scheduled_for: string | null;
  source: string | null;
  has_image: boolean | null;
  media_id: string | null;
  media_ids?: string[] | null;
  created_at: string;
  connection_id: string | null;
  metadata?: Record<string, unknown> | null;
  media_library?: { url: string; mime_type?: string | null } | { url: string; mime_type?: string | null }[] | null;
};

type HistoryRow = {
  id: string;
  platform: string;
  content: string | null;
  created_at: string;
  connection_id: string | null;
  external_id?: string | null;
  metadata: Record<string, unknown> | null;
};

function mediaUrlFromJoin(rel: DraftRow['media_library']): string | null {
  if (!rel) return null;
  if (Array.isArray(rel)) return rel[0]?.url || null;
  return rel.url || null;
}

function mediaMimeFromJoin(rel: DraftRow['media_library']): string | null {
  if (!rel) return null;
  const row = Array.isArray(rel) ? rel[0] : rel;
  return row?.mime_type || null;
}

const isVideoUrl = (url: string | null, mime: string | null) =>
  /^video\//i.test(mime || '') || /\.(mp4|mov|m4v|webm)(\?|#|$)/i.test(url || '');

function mapDraftRow(
  r: DraftRow,
  urlById: Map<string, string>,
): UpcomingSocialPostSnippet | null {
  if (r.platform !== 'facebook' && r.platform !== 'instagram' && r.platform !== 'linkedin') return null;
  const joined = mediaUrlFromJoin(r.media_library);
  const imageUrl = joined || (r.media_id ? urlById.get(r.media_id) || null : null);
  const mediaIds =
    Array.isArray(r.media_ids) && r.media_ids.length > 0
      ? r.media_ids
      : r.media_id
        ? [r.media_id]
        : null;
  const imageCount = mediaIds?.length || (imageUrl ? 1 : 0);
  const meta = r.metadata && typeof r.metadata === 'object' ? r.metadata : {};
  const formatRaw = String(meta.postFormat || 'feed').toLowerCase();
  const isVideo = isVideoUrl(imageUrl, mediaMimeFromJoin(r.media_library));
  return {
    id: r.id,
    platform: r.platform as SocialPlatform,
    content: r.content,
    scheduled_for: r.scheduled_for,
    source: r.source,
    has_image: !!(r.has_image || imageUrl || imageCount > 0),
    media_id: r.media_id,
    media_ids: mediaIds,
    image_count: imageCount,
    image_url: imageUrl,
    created_at: r.created_at,
    connection_id: r.connection_id,
    status: 'pending',
    is_video: isVideo,
    post_format: formatRaw === 'reel' || formatRaw === 'story' ? formatRaw : 'feed',
    video_caption_pending: isVideo && meta.videoCaptionPending === true,
    video_thumbnail_pending: isVideo && meta.videoThumbnailPending === true,
  };
}

function mapHistoryRow(r: HistoryRow): UpcomingSocialPostSnippet | null {
  if (r.platform !== 'facebook' && r.platform !== 'instagram' && r.platform !== 'linkedin') return null;
  const meta = r.metadata && typeof r.metadata === 'object' ? r.metadata : {};
  if (String(meta.status || '') === 'deleted') return null;
  const mediaUrls = Array.isArray(meta.media_urls)
    ? meta.media_urls.map((u) => String(u || '').trim()).filter(Boolean)
    : [];
  const mediaUrl =
    (typeof meta.media_url === 'string' && meta.media_url) || mediaUrls[0] || null;
  const mediaIds = Array.isArray(meta.media_ids)
    ? meta.media_ids.map((id) => String(id || '').trim()).filter(Boolean)
    : typeof meta.media_id === 'string' && meta.media_id
      ? [meta.media_id]
      : null;
  const imageCount =
    Number(meta.image_count) ||
    mediaIds?.length ||
    mediaUrls.length ||
    (mediaUrl ? 1 : 0);
  const formatRaw = String(meta.post_format || 'feed').toLowerCase();
  const sourceRaw = typeof meta.source === 'string' ? meta.source : null;
  const source =
    sourceRaw === 'manual' || sourceRaw === 'ai_approved' || sourceRaw === 'ai_auto'
      ? sourceRaw === 'manual'
        ? 'manual'
        : 'ai'
      : sourceRaw;

  return {
    id: r.id,
    platform: r.platform as SocialPlatform,
    content: String(r.content || ''),
    scheduled_for: r.created_at,
    source,
    has_image: !!(meta.has_image || mediaUrl || imageCount > 0),
    media_id: mediaIds?.[0] || null,
    media_ids: mediaIds,
    image_count: imageCount,
    image_url: mediaUrl,
    created_at: r.created_at,
    connection_id: r.connection_id,
    status: 'published',
    is_video: meta.has_video === true || isVideoUrl(mediaUrl, null),
    post_format: formatRaw === 'reel' || formatRaw === 'story' ? formatRaw : 'feed',
    ayrshare_post_id: ayrshareIdFromHistory(meta, r.external_id),
  };
}

function ayrshareIdFromHistory(
  meta: Record<string, unknown>,
  externalId: string | null | undefined,
): string | null {
  const ayrshare = typeof meta.ayrshare_id === 'string' ? meta.ayrshare_id.trim() : '';
  if (ayrshare) return ayrshare;
  const external = String(externalId || '').trim();
  const platformPostId = typeof meta.platform_post_id === 'string' ? meta.platform_post_id.trim() : '';
  if (external && external !== platformPostId) return external;
  return null;
}

type UseUpcomingSocialPostsOptions = {
  /** Max pending drafts to return (default 20). Use a higher value for calendars. */
  limit?: number;
  platform?: SocialPlatform;
  /**
   * When true, also include published posts from history (green “completed” on the calendar).
   * Pending-only surfaces (upcoming queue) should leave this false.
   */
  includePublished?: boolean;
  /** How far back to load published history when includePublished (default 60 days). */
  publishedLookbackDays?: number;
};

/**
 * Facebook / Instagram posts for Marketing calendar and upcoming panels.
 * By default returns staged/scheduled drafts only (status = pending).
 */
export function useUpcomingSocialPostsSnippet(options: UseUpcomingSocialPostsOptions = {}) {
  const {
    limit = 20,
    platform,
    includePublished = false,
    publishedLookbackDays = 60,
  } = options;
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const [posts, setPosts] = useState<UpcomingSocialPostSnippet[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!SOCIAL_ENABLED || !businessId) {
      setPosts([]);
      setLoading(false);
      return;
    }

    if (!opts.silent) setLoading(true);
    try {
      const attempts = [
        'id, platform, content, scheduled_for, source, has_image, media_id, media_ids, created_at, connection_id, metadata, media_library!social_post_drafts_media_id_fkey(url, mime_type)',
        'id, platform, content, scheduled_for, source, has_image, media_id, media_ids, created_at, connection_id, media_library!social_post_drafts_media_id_fkey(url)',
        'id, platform, content, scheduled_for, source, has_image, media_id, media_ids, created_at, connection_id, media_library!media_id(url)',
        'id, platform, content, scheduled_for, source, has_image, media_id, media_ids, created_at, connection_id',
        'id, platform, content, scheduled_for, source, has_image, media_id, created_at, connection_id, media_library!social_post_drafts_media_id_fkey(url)',
        'id, platform, content, scheduled_for, source, has_image, media_id, created_at, connection_id',
      ];

      let rows: DraftRow[] = [];
      let lastError: unknown = null;
      for (const select of attempts) {
        let query = supabase
          .from('social_post_drafts')
          .select(select)
          .eq('business_id', businessId)
          .eq('status', 'pending')
          .order('scheduled_for', { ascending: true, nullsFirst: false })
          .limit(limit);
        if (platform) {
          query = query.eq('platform', platform);
        }
        const { data, error } = await query;
        if (!error) {
          rows = (data as DraftRow[]) || [];
          lastError = null;
          break;
        }
        lastError = error;
      }
      if (lastError) throw lastError;

      const missingIds = rows
        .filter((r) => r.media_id && !mediaUrlFromJoin(r.media_library))
        .map((r) => r.media_id as string);

      const urlById = new Map<string, string>();
      if (missingIds.length > 0) {
        const { data: mediaRows } = await supabase
          .from('media_library')
          .select('id, url')
          .in('id', [...new Set(missingIds)]);
        for (const m of mediaRows || []) {
          urlById.set(m.id as string, m.url as string);
        }
      }

      const pending = rows
        .map((r) => mapDraftRow(r, urlById))
        .filter(Boolean) as UpcomingSocialPostSnippet[];

      let published: UpcomingSocialPostSnippet[] = [];
      if (includePublished) {
        const since = new Date();
        since.setDate(since.getDate() - Math.max(1, publishedLookbackDays));
        let histQuery = supabase
          .from('social_post_history')
          .select('id, platform, content, created_at, connection_id, external_id, metadata')
          .eq('business_id', businessId)
          .gte('created_at', since.toISOString())
          .order('created_at', { ascending: false })
          .limit(Math.max(limit, 100));
        if (platform) {
          histQuery = histQuery.eq('platform', platform);
        }
        const { data: histRows, error: histErr } = await histQuery;
        if (histErr) {
          console.warn('Failed to load published social posts for calendar:', histErr);
        } else {
          published = ((histRows as HistoryRow[]) || [])
            .map(mapHistoryRow)
            .filter(Boolean) as UpcomingSocialPostSnippet[];
        }
      }

      const waiting = pending.find((post) => post.video_caption_pending || post.video_thumbnail_pending);
      if (waiting && businessId) requestVideoFinish(businessId, waiting.id);

      setPosts([...pending, ...published]);
    } catch (err) {
      console.error('Failed to load upcoming social posts:', err);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [businessId, limit, platform, includePublished, publishedLookbackDays]);

  const refresh = useCallback(() => load(), [load]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live: refetch (without a loading flash) when posts change server-side —
  // AI rescheduling, the cron publisher, or edits from another device.
  useBusinessTableChanges(businessId, SOCIAL_LIVE_TABLES, () => void load({ silent: true }), {
    enabled: SOCIAL_ENABLED,
  });

  return { posts, loading, refresh };
}
